import * as React from "react";
import { Link } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ClientNameReveal } from "@/components/clients/ClientNameReveal";
import { useSyncLedger } from "@/hooks/queries/use-sync-ledger";
import { useListEvaluations } from "@/hooks/queries/useListEvaluations";
import { useListClients } from "@/hooks/queries/useListClients";
import type { ClientRecord } from "@/hooks/queries/client-types";
import { LEDGER_STATUS_META, type LedgerEntry } from "@/lib/sync-ledger";

// SyncLedger.tsx (Story 7.6) — the "Submitted" tab body: a passive, read-only record of what
// happened to every evaluation worked on offline. It reads `useSyncLedger` (reconcile-then-read over
// IndexedDB) and renders one row per tracked evaluation with a status pill + PHI-free detail drawer.
//
// No hi-fi mockup exists for this surface (a documented, accepted decision — Epic 7 QA scenarios doc).
// Built from the design system + the EvaluationsListScreen / ConflictResolver idioms: `--bg-primary`
// canvas (never #fff), muted/desaturated Badge variants (never clinical-alarm), tabular-nums on the
// timestamp, real <button> pills, the shadcn/Radix Dialog for focus-trap + Escape + aria-modal.
//
// PHI discipline: the only client identity is the on-demand `ClientNameReveal` join (never bulk
// decrypt); the detail drawer carries ONLY sync metadata (status, version, error excerpt, timestamp)
// — never a name, score, or clinical note.

// A generous single page to resolve eval → client identity (mirrors EvaluationsListScreen's join
// caveat). An offline eval whose create op has NOT yet synced won't be in the server list; that row
// falls back to its evaluation id (always available, PHI-free) until it syncs and joins.
//
// LIMITATION (PR #441 review — womanonrails): unlike the parent evaluations list, which paginates and
// whose JOIN ceiling therefore matches the visible page, the ledger lists EVERY tracked eval from IDB
// (unbounded). A clinician with >100 server evaluations has entries beyond the first 100 silently fall
// back to the "Offline evaluation" / eval-id placeholder even when the server has a row — the limit is
// invisible. PHI-safe (no identity is ever wrongly disclosed; the name simply isn't resolved). The
// structural fix (cursor pagination or a batch eval→client lookup) is the SAME deferred item as 6.9's
// CLIENT_JOIN_LIMIT (deferred-work.md) and awaits a batch client-by-ids endpoint; revisit when a
// >100-tracked-eval caseload makes the missing name a real UX issue.
const JOIN_LIMIT = 100;

/** Compact date + time for the transition timestamp; "—" when absent/unparseable. Pair with
 *  `tabular-nums` at the call site. */
function formatLedgerTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function SyncLedger() {
  const { entries } = useSyncLedger();

  // eval_id → client_id (server source of truth) + client_id → record (for the erased tombstone).
  const evalsQuery = useListEvaluations({ page: 1, limit: JOIN_LIMIT });
  const clientsQuery = useListClients({ limit: JOIN_LIMIT });

  const clientIdByEval = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const e of evalsQuery.data?.results ?? []) map.set(e.id, e.client_id);
    return map;
  }, [evalsQuery.data]);

  const clientsById = React.useMemo(() => {
    const map = new Map<string, ClientRecord>();
    for (const c of clientsQuery.data?.results ?? []) map.set(c.id, c);
    return map;
  }, [clientsQuery.data]);

  // The detail drawer is controlled by the selected entry; Radix restores focus to the originating
  // pill on close (the click focused it).
  const [selected, setSelected] = React.useState<LedgerEntry | null>(null);

  // --- aria-live delta announcer (AC11) -------------------------------------------------------
  // Announce ONLY a status TRANSITION of an already-tracked evaluation (e.g. queued → sent). A row
  // first APPEARING is silent — that keeps a reload (which re-populates every existing row from IDB)
  // from reading the whole list out, while still announcing the meaningful "your queued edit just
  // synced / conflicted" change the clinician is waiting on.
  const prevStatusRef = React.useRef<Map<string, string>>(new Map());
  const [announcement, setAnnouncement] = React.useState("");

  React.useEffect(() => {
    const prev = prevStatusRef.current;
    const changedLabels: string[] = [];
    for (const e of entries) {
      const before = prev.get(e.eval_id);
      if (before !== undefined && before !== e.status) {
        changedLabels.push(LEDGER_STATUS_META[e.status].label);
      }
    }
    prevStatusRef.current = new Map(entries.map((e) => [e.eval_id, e.status]));

    if (changedLabels.length === 1) {
      setAnnouncement(`Evaluation sync status changed to ${changedLabels[0]}.`);
    } else if (changedLabels.length > 1) {
      setAnnouncement(`${changedLabels.length} evaluation sync statuses updated.`);
    }
  }, [entries]);

  return (
    <section className="flex flex-col gap-4" aria-label="Sync ledger">
      {/* Visually-hidden polite live region — announces status deltas to screen-reader users.
          `role="status"` implies aria-live="polite"+aria-atomic="true" per the ARIA spec; the explicit
          attrs are KEPT intentionally as defensive redundancy for older/inconsistent AT (the same
          belt-and-suspenders the design system applies to dynamic-text Banner regions). Do not "clean
          up" as redundant. [PR #441 review — womanonrails, decision recorded 2026-06-30] */}
      <span className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {announcement}
      </span>

      <p className="text-sm text-[color:var(--text-tertiary)]">
        The sync state of every evaluation you worked on offline. Nothing here changes your data —
        it's a record of what reached the server.
      </p>

      {entries.length === 0 ? (
        <div className="rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] px-6 py-12 text-center text-sm text-[color:var(--text-tertiary)]">
          Nothing has needed offline sync yet.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-none border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)]">
          <table className="w-full border-collapse text-left text-sm">
            <caption className="sr-only">
              Offline sync status of your evaluations. Activate a status to see details.
            </caption>
            <thead>
              <tr className="border-b border-[color:var(--bg-quaternary)] text-xs uppercase tracking-wide text-[color:var(--text-tertiary)]">
                <th scope="col" className="px-4 py-3 font-semibold">Client</th>
                <th scope="col" className="px-4 py-3 font-semibold">Last update</th>
                <th scope="col" className="px-4 py-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <LedgerRow
                  key={entry.eval_id}
                  entry={entry}
                  clientId={clientIdByEval.get(entry.eval_id)}
                  client={
                    clientIdByEval.get(entry.eval_id)
                      ? clientsById.get(clientIdByEval.get(entry.eval_id) as string)
                      : undefined
                  }
                  onOpenDetail={() => setSelected(entry)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <LedgerDetailDrawer entry={selected} onClose={() => setSelected(null)} />
    </section>
  );
}

function LedgerRow({
  entry,
  clientId,
  client,
  onOpenDetail,
}: {
  entry: LedgerEntry;
  clientId: string | undefined;
  client: ClientRecord | undefined;
  onOpenDetail: () => void;
}) {
  const meta = LEDGER_STATUS_META[entry.status];
  return (
    <tr className="border-b border-[color:var(--bg-quaternary)] last:border-b-0 align-top">
      <td className="px-4 py-3">
        <div className="flex flex-col gap-1">
          {clientId ? (
            <ClientNameReveal
              clientId={clientId}
              initialErased={Boolean(client?.gdpr_erased_at)}
              erasedAt={client?.gdpr_erased_at ?? null}
            />
          ) : (
            // Unsynced offline eval: no server row to join yet — show the evaluation id (PHI-free).
            <span className="font-medium text-[color:var(--text-secondary)]">
              Offline evaluation
            </span>
          )}
          <span
            className="text-xs text-[color:var(--text-tertiary)]"
            title={entry.eval_id}
          >
            Evaluation ID: {entry.eval_id}
          </span>
        </div>
      </td>
      <td className="px-4 py-3 align-middle tabular-nums text-[color:var(--text-secondary)]">
        {formatLedgerTime(entry.updated_at)}
      </td>
      <td className="px-4 py-3 align-middle">
        {/* AC8/AC10 — the pill is a real focusable <button> that opens the detail drawer. */}
        <button
          type="button"
          onClick={onOpenDetail}
          className="rounded-[var(--radius-full)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--focus-ring)]"
          aria-label={`${meta.label} — view sync details`}
        >
          <Badge variant={meta.pillVariant}>{meta.label}</Badge>
        </button>
      </td>
    </tr>
  );
}

/** The PHI-free detail drawer (AC8/AC9). Shows the status, the sync metadata excerpt, and the
 *  transition timestamp; a `Conflict` row links out to the evaluation where Story 7.5 resolves it. */
function LedgerDetailDrawer({
  entry,
  onClose,
}: {
  entry: LedgerEntry | null;
  onClose: () => void;
}) {
  const meta = entry ? LEDGER_STATUS_META[entry.status] : null;
  return (
    <Dialog
      open={entry !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent showClose closeLabel="Close sync details" className="w-[min(var(--width-md,520px),100%)]">
        {entry && meta ? (
          <>
            <DialogHeader>
              <DialogTitle>Sync details</DialogTitle>
              <DialogDescription>
                What happened to this evaluation's offline changes. No patient data is shown here.
              </DialogDescription>
            </DialogHeader>

            <dl className="flex flex-col gap-3 text-sm">
              <div className="flex items-center gap-3">
                <dt className="w-28 shrink-0 text-[color:var(--text-tertiary)]">Status</dt>
                <dd>
                  <Badge variant={meta.pillVariant}>{meta.label}</Badge>
                </dd>
              </div>
              <div className="flex gap-3">
                <dt className="w-28 shrink-0 text-[color:var(--text-tertiary)]">Detail</dt>
                <dd className="text-[color:var(--text-secondary)]">{entry.detail}</dd>
              </div>
              <div className="flex items-center gap-3">
                <dt className="w-28 shrink-0 text-[color:var(--text-tertiary)]">Last update</dt>
                <dd className="tabular-nums text-[color:var(--text-secondary)]">
                  {formatLedgerTime(entry.updated_at)}
                </dd>
              </div>
              <div className="flex items-center gap-3">
                <dt className="w-28 shrink-0 text-[color:var(--text-tertiary)]">Evaluation ID</dt>
                <dd className="break-all text-[color:var(--text-tertiary)]">
                  {entry.eval_id}
                </dd>
              </div>
            </dl>

            {entry.status === "conflict" ? (
              <DialogFooter>
                {/* AC9 — the ledger's ONLY navigational affordance: open the evaluation where the
                    Story 7.5 ConflictResolver resolves it. The ledger surfaces, never resolves inline. */}
                <Button asChild variant="primary">
                  <Link to={`/evaluations/${entry.eval_id}`}>Open to resolve</Link>
                </Button>
              </DialogFooter>
            ) : null}
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export default SyncLedger;
